import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { FormulaEditor } from './FormulaEditor';
import type { IndicatorCategory, IndicatorComparator, IndicatorFrequency, IndicatorPayload } from './types';
import { COMPARATOR_LABEL, FREQUENCY_LABEL, INDICATOR_CATEGORY_LABEL } from './objectiveUtils';

const COMPARATORS: IndicatorComparator[] = ['gte', 'lte', 'eq', 'between'];
const CATEGORIES: IndicatorCategory[] = ['calidad', 'ambiental'];
const FREQUENCIES: IndicatorFrequency[] = [
  'mensual',
  'bimestral',
  'trimestral',
  'semestral',
  'anual',
];

interface IndicatorFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  objectiveId: string;
  initial?: Indicator | null;
  onSubmit: (payload: IndicatorPayload) => Promise<void>;
}

export function IndicatorFormModal({
  open,
  onOpenChange,
  objectiveId,
  initial,
  onSubmit,
}: IndicatorFormModalProps) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [formula, setFormula] = useState('');
  const [comparator, setComparator] = useState<IndicatorComparator>('gte');
  const [targetValue, setTargetValue] = useState('');
  const [lowerLimit, setLowerLimit] = useState('');
  const [upperLimit, setUpperLimit] = useState('');
  const [frequency, setFrequency] = useState<IndicatorFrequency>('mensual');
  const [category, setCategory] = useState<IndicatorCategory>('calidad');
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertRoles, setAlertRoles] = useState('admin');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setUnit(initial?.unit ?? '');
    setFormula(initial?.formula ?? '');
    setComparator(initial?.comparator ?? 'gte');
    setTargetValue(initial?.targetValue != null ? String(initial.targetValue) : '');
    setLowerLimit(initial?.lowerLimit != null ? String(initial.lowerLimit) : '');
    setUpperLimit(initial?.upperLimit != null ? String(initial.upperLimit) : '');
    setFrequency(initial?.frequency ?? 'mensual');
    setCategory(initial?.category ?? 'calidad');
    setAlertEnabled(initial?.alertEnabled ?? false);
    setAlertRoles(initial?.alertRoles ?? 'admin');
  }, [open, initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        objectiveId,
        name: name.trim(),
        unit: unit.trim() || null,
        formula: formula.trim() || null,
        comparator,
        targetValue: comparator === 'between' ? null : Number.parseFloat(targetValue),
        lowerLimit: comparator === 'between' ? Number.parseFloat(lowerLimit) : null,
        upperLimit: comparator === 'between' ? Number.parseFloat(upperLimit) : null,
        frequency,
        category,
        alertEnabled,
        alertRoles: alertEnabled ? alertRoles.trim() : null,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? 'Editar indicador' : 'Nuevo indicador'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Nombre</label>
            <input
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={255}
              placeholder="Ej: Tasa de defectos"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Unidad</label>
              <input
                className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="%"
                maxLength={40}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Categoría</label>
              <select
                className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
                value={category}
                onChange={(e) => setCategory(e.target.value as IndicatorCategory)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {INDICATOR_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Frecuencia</label>
            <select
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as IndicatorFrequency)}
            >
              {FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {FREQUENCY_LABEL[f]}
                </option>
              ))}
            </select>
          </div>
          <FormulaEditor value={formula} onChange={setFormula} />
          <div>
            <label className="text-sm font-medium">Comparador</label>
            <select
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
              value={comparator}
              onChange={(e) => setComparator(e.target.value as IndicatorComparator)}
            >
              {COMPARATORS.map((c) => (
                <option key={c} value={c}>
                  {COMPARATOR_LABEL[c]}
                </option>
              ))}
            </select>
          </div>
          {comparator === 'between' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Límite inferior</label>
                <input
                  type="number"
                  step="any"
                  className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
                  value={lowerLimit}
                  onChange={(e) => setLowerLimit(e.target.value)}
                  required
                  placeholder="Ej: 80"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Límite superior</label>
                <input
                  type="number"
                  step="any"
                  className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
                  value={upperLimit}
                  onChange={(e) => setUpperLimit(e.target.value)}
                  required
                  placeholder="Ej: 100"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium">Valor meta</label>
              <input
                type="number"
                step="any"
                className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                required
                placeholder="Ej: 95"
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={alertEnabled}
              onChange={(e) => setAlertEnabled(e.target.checked)}
            />
            Alertar si sale de meta
          </label>
          {alertEnabled && (
            <div>
              <label className="text-sm font-medium">Roles a notificar (CSV)</label>
              <input
                className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
                value={alertRoles}
                onChange={(e) => setAlertRoles(e.target.value)}
                placeholder="admin,superadmin"
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
